import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/auth'

/** Read and clear a one-time sign-in link from the URL fragment (never sent to servers or logs). */
function takeLinkFromFragment(): { challengeId: string; token: string } | null {
  if (typeof window === 'undefined' || !window.location.hash) return null
  const params = new URLSearchParams(window.location.hash.slice(1))
  const challengeId = params.get('challenge')
  const token = params.get('token')
  if (!challengeId || !token) return null
  window.history.replaceState(null, '', window.location.pathname + window.location.search)
  return { challengeId, token }
}

type Requested = { id: string; at: number; resendAfterMs: number }
const CHALLENGES_KEY = 'afos360.email_challenges'
const CODE_LIFETIME_MS = 10 * 60 * 1000

/** Challenge IDs this tab was given per address, newest first, within the code lifetime. */
function loadRequested(email: string): Requested[] {
  try {
    const all = JSON.parse(sessionStorage.getItem(CHALLENGES_KEY) || '{}') as Record<string, Requested[]>
    const now = Date.now()
    return (all[email] || []).filter((r) => now - r.at < CODE_LIFETIME_MS).slice(0, 5)
  } catch {
    return []
  }
}

function saveRequested(email: string, list: Requested[]) {
  try {
    const all = JSON.parse(sessionStorage.getItem(CHALLENGES_KEY) || '{}') as Record<string, Requested[]>
    all[email] = list.slice(0, 5)
    sessionStorage.setItem(CHALLENGES_KEY, JSON.stringify(all))
  } catch {
    /* storage unavailable: the newest mail's link still works */
  }
}

/** Email one-time-code sign-in: request a code, then enter it, or open the link in the mail. */
export function EmailSignIn() {
  const { emailSignIn, startEmailSignIn, verifyEmailSignIn, redeemEmailLink } = useAuth()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [notInvited, setNotInvited] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const linkTaken = useRef(false)
  const [pendingLink, setPendingLink] = useState<{ challengeId: string; token: string } | null>(null)

  useEffect(() => {
    if (!emailSignIn || linkTaken.current) return
    linkTaken.current = true
    // Never redeem on open: the person confirms the address first (no login CSRF).
    const link = takeLinkFromFragment()
    if (link) setPendingLink(link)
  }, [emailSignIn])

  if (!emailSignIn) return null

  async function confirmLink(event: FormEvent) {
    event.preventDefault()
    if (!pendingLink) return
    setBusy(true)
    setError(null)
    try {
      await redeemEmailLink(email.trim().toLowerCase(), pendingLink.challengeId, pendingLink.token)
    } catch (err) {
      setError(readable(err, 'That sign-in link did not work for this address. Request a new code.'))
    } finally {
      setBusy(false)
    }
  }

  async function requestCode(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const address = email.trim().toLowerCase()
    const earlier = loadRequested(address)
    const newest = earlier[0]
    if (newest && Date.now() - newest.at < newest.resendAfterMs) {
      // Inside the resend spacing no new mail would go out: keep the current code.
      setChallengeId(newest.id)
      setNotice('A code was sent recently. Use the newest sign-in email, or its link.')
      setBusy(false)
      return
    }
    try {
      const started = await startEmailSignIn(address)
      if (started.status === 'not_invited' || !started.challenge_id) {
        // Invite only: no code exists for this address; send them to the access request.
        setNotInvited(started.message)
        return
      }
      const entry = {
        id: started.challenge_id,
        at: Date.now(),
        resendAfterMs: Math.max(0, Number(started.resend_after_s || 0)) * 1000,
      }
      saveRequested(address, [entry, ...earlier])
      setChallengeId(started.challenge_id)
      setNotice(started.message)
    } catch (err) {
      setError(readable(err, 'Could not send a code. Try again shortly.'))
    } finally {
      setBusy(false)
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault()
    if (!challengeId) return
    setBusy(true)
    setError(null)
    try {
      const address = email.trim().toLowerCase()
      const previous = loadRequested(address)
        .map((r) => r.id)
        .filter((id) => id !== challengeId)
      await verifyEmailSignIn(address, challengeId, code.trim(), previous)
    } catch (err) {
      setError(readable(err, 'That code did not work. Check it or request a new one.'))
    } finally {
      setBusy(false)
    }
  }

  const field =
    'w-full rounded-full border border-[var(--color-line)] bg-black/30 px-5 py-3 text-[var(--color-foam)] placeholder:text-[var(--color-mist)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-cyan)]'
  const button =
    'neon-ring w-full rounded-full bg-[var(--color-foam)] px-5 py-3 font-semibold text-[var(--color-void)] disabled:opacity-60'

  if (pendingLink) {
    return (
      <div className="space-y-3">
        <form onSubmit={confirmLink} className="space-y-3" aria-label="Confirm your sign-in link">
          <p className="text-center text-sm text-[var(--color-mist)]">
            To finish signing in, enter the email address this link was sent to.
          </p>
          <label htmlFor="afos-link-email" className="sr-only">
            Email the link was sent to
          </label>
          <input
            id="afos-link-email"
            type="email"
            autoComplete="email"
            required
            maxLength={254}
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={field}
          />
          <button type="submit" disabled={busy || !email.trim()} className={button}>
            {busy ? 'Signing in…' : 'Confirm and sign in'}
          </button>
          <button
            type="button"
            onClick={() => {
              setPendingLink(null)
              setError(null)
            }}
            className="w-full text-center text-sm text-[var(--color-cyan)] hover:underline"
          >
            Not you? Sign in another way
          </button>
        </form>
        {error && (
          <p role="alert" className="text-center text-sm text-[var(--color-rose)]">
            {error}
          </p>
        )}
      </div>
    )
  }

  if (notInvited) {
    const address = email.trim().toLowerCase()
    return (
      <div className="space-y-3 text-center" role="status">
        <p className="text-sm font-semibold text-[var(--color-foam)]">You need an invite to sign in</p>
        <p className="text-sm text-[var(--color-mist)]">{notInvited}</p>
        <Link to={`/signup?email=${encodeURIComponent(address)}`} className={`${button} block`}>
          Request early access
        </Link>
        <button
          type="button"
          onClick={() => setNotInvited(null)}
          className="w-full text-center text-sm text-[var(--color-cyan)] hover:underline"
        >
          Use a different email
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {!challengeId ? (
        <form onSubmit={requestCode} className="space-y-3" aria-label="Request a sign-in code">
          <label htmlFor="afos-email" className="sr-only">
            Work email
          </label>
          <input
            id="afos-email"
            type="email"
            autoComplete="email"
            required
            maxLength={254}
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={field}
          />
          <button type="submit" disabled={busy || !email.trim()} className={button}>
            {busy ? 'Sending…' : 'Email me a sign-in code'}
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode} className="space-y-3" aria-label="Enter your sign-in code">
          {notice && <p className="text-center text-sm text-[var(--color-mist)]">{notice}</p>}
          <label htmlFor="afos-code" className="sr-only">
            Sign-in code
          </label>
          <input
            id="afos-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6,12}"
            required
            maxLength={12}
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className={`${field} text-center tracking-[0.4em]`}
          />
          <button type="submit" disabled={busy || code.length < 6} className={button}>
            {busy ? 'Verifying…' : 'Sign in'}
          </button>
          <button
            type="button"
            onClick={() => {
              setChallengeId(null)
              setCode('')
              setNotice(null)
              setError(null)
            }}
            className="w-full text-center text-sm text-[var(--color-cyan)] hover:underline"
          >
            Use a different email or send a new code
          </button>
        </form>
      )}
      {error && (
        <p role="alert" className="text-center text-sm text-[var(--color-rose)]">
          {error}
        </p>
      )}
    </div>
  )
}

function readable(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback
  try {
    const parsed = JSON.parse(err.message) as { detail?: unknown }
    return typeof parsed.detail === 'string' ? parsed.detail : fallback
  } catch {
    return err.message.length < 160 ? err.message : fallback
  }
}
